import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import {
    AppBar,
    Alert,
    Backdrop,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fade,
    Fab,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Paper,
    Rating,
    Select,
    Skeleton,
    Snackbar,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tabs,
    TextField,
    Toolbar,
    Tooltip,
    Typography
} from '@mui/material';

import {
    Add,
    AddShoppingCart,
    ArrowBack,
    Cancel,
    Category,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Close,
    Diamond,
    Email,
    Facebook,
    Favorite,
    FavoriteBorder,
    Google,
    Home,
    Info,
    KeyboardArrowRight,
    LocalShipping,
    LocationOn,
    Lock,
    Menu,
    Person,
    Phone,
    Remove,
    RemoveShoppingCart,
    Scale,
    Security,
    Share,
    ShoppingBag,
    ShoppingCart,
    Style,
    VerifiedUser,
    Visibility,
    VisibilityOff,
    WbSunny,
    ZoomIn
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import AppHeader from '../components/AppHeader';
import MobileDrawer from '../components/MobileDrawer';
import AppFooter from '../components/AppFooter';
import AuthModal from '../components/AuthModal';

const BASE_URL = 'https://dhahabjw.com';

// Enhanced Image Gallery Component
// Mobile Optimized Image Gallery Component
const ImageGallery = ({ images, productName }) => {
    const [selectedImage, setSelectedImage] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Handle touch events for swipe navigation
    const handleTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && selectedImage < images.length - 1) {
            setSelectedImage(selectedImage + 1);
            setImageLoading(true);
        }
        if (isRightSwipe && selectedImage > 0) {
            setSelectedImage(selectedImage - 1);
            setImageLoading(true);
        }
    };

    const handlePrevImage = () => {
        if (selectedImage > 0) {
            setSelectedImage(selectedImage - 1);
            setImageLoading(true);
        }
    };

    const handleNextImage = () => {
        if (selectedImage < images.length - 1) {
            setSelectedImage(selectedImage + 1);
            setImageLoading(true);
        }
    };

    if (!images || images.length === 0) {
        return (
            <Card sx={{ mb: { xs: 2, md: 3 } }}>
                <Skeleton variant="rectangular" height={{ xs: 250, sm: 350, md: 500 }} />
            </Card>
        );
    }

    return (
        <Box>
            {/* Main Image Container */}
            <Card sx={{
                mb: { xs: 2, md: 3 },
                position: 'relative',
                overflow: 'hidden',
                borderRadius: { xs: 2, md: 3 }
            }}>
                {imageLoading && (
                    <Skeleton
                        variant="rectangular"
                        height={{ xs: 250, sm: 350, md: 500 }}
                        sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                    />
                )}

                <CardMedia
                    component="img"
                    height="auto"
                    image={images[selectedImage]?.url}
                    alt={images[selectedImage]?.alt || productName}
                    sx={{
                        objectFit: 'contain',
                        cursor: 'zoom-in',
                        transition: 'transform 0.3s ease',
                        width: '100%',
                        userSelect: 'none',
                        '&:hover': {
                            transform: { md: 'scale(1.02)' }
                        }
                    }}
                    onClick={() => setIsZoomed(true)}
                    onLoad={() => setImageLoading(false)}
                    onError={() => setImageLoading(false)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                />

                {/* Navigation Arrows - Show on mobile when multiple images */}
                {images.length > 1 && (
                    <>
                        {/* Previous Button */}
                        <IconButton
                            sx={{
                                position: 'absolute',
                                left: { xs: 8, sm: 16 },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(0,0,0,0.6)',
                                backdropFilter: 'blur(10px)',
                                color: 'white',
                                width: { xs: 36, sm: 44 },
                                height: { xs: 36, sm: 44 },
                                opacity: selectedImage === 0 ? 0.3 : 1,
                                '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.8)',
                                    transform: 'translateY(-50%) scale(1.1)'
                                },
                                '&:disabled': {
                                    opacity: 0.3
                                }
                            }}
                            onClick={handlePrevImage}
                            disabled={selectedImage === 0}
                        >
                            <ChevronLeft sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                        </IconButton>

                        {/* Next Button */}
                        <IconButton
                            sx={{
                                position: 'absolute',
                                right: { xs: 8, sm: 16 },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(0,0,0,0.6)',
                                backdropFilter: 'blur(10px)',
                                color: 'white',
                                width: { xs: 36, sm: 44 },
                                height: { xs: 36, sm: 44 },
                                opacity: selectedImage === images.length - 1 ? 0.3 : 1,
                                '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.8)',
                                    transform: 'translateY(-50%) scale(1.1)'
                                },
                                '&:disabled': {
                                    opacity: 0.3
                                }
                            }}
                            onClick={handleNextImage}
                            disabled={selectedImage === images.length - 1}
                        >
                            <ChevronRight sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                        </IconButton>
                    </>
                )}

                {/* Zoom Button */}
                <IconButton
                    sx={{
                        position: 'absolute',
                        top: { xs: 8, sm: 16 },
                        right: { xs: 8, sm: 16 },
                        bgcolor: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(10px)',
                        width: { xs: 36, sm: 44 },
                        height: { xs: 36, sm: 44 },
                        '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.8)',
                            transform: 'scale(1.1)'
                        }
                    }}
                    onClick={() => setIsZoomed(true)}
                >
                    <ZoomIn sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                </IconButton>

                {/* Image Counter - Mobile */}
                {images.length > 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: { xs: 8, sm: 16 },
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: 'rgba(0,0,0,0.7)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: 2,
                            px: { xs: 1.5, sm: 2 },
                            py: { xs: 0.5, sm: 1 },
                            display: { xs: 'block', md: 'none' }
                        }}
                    >
                        <Typography variant="caption" sx={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}>
                            {selectedImage + 1} / {images.length}
                        </Typography>
                    </Box>
                )}
            </Card>

            {/* Image Zoom Dialog */}
            <Dialog
                open={isZoomed}
                onClose={() => setIsZoomed(false)}
                maxWidth="lg"
                fullWidth
                fullScreen={{ xs: true, sm: false }}
                PaperProps={{
                    sx: {
                        bgcolor: { xs: 'black', sm: 'transparent' },
                        boxShadow: 'none',
                        overflow: 'hidden',
                        m: { xs: 0, sm: 2 }
                    }
                }}
            >
                <DialogContent sx={{
                    p: 0,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: { xs: '100vh', sm: 'auto' }
                }}>
                    {/* Close Button */}
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: { xs: 16, sm: 16 },
                            right: { xs: 16, sm: 16 },
                            bgcolor: 'rgba(0,0,0,0.8)',
                            color: 'white',
                            zIndex: 2,
                            width: { xs: 44, sm: 48 },
                            height: { xs: 44, sm: 48 },
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.9)',
                                transform: 'scale(1.1)'
                            }
                        }}
                        onClick={() => setIsZoomed(false)}
                    >
                        <Close sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                    </IconButton>

                    {/* Navigation in Zoom - Multiple Images */}
                    {images.length > 1 && (
                        <>
                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    left: { xs: 16, sm: 24 },
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(0,0,0,0.8)',
                                    color: 'white',
                                    zIndex: 2,
                                    width: { xs: 44, sm: 48 },
                                    height: { xs: 44, sm: 48 },
                                    opacity: selectedImage === 0 ? 0.3 : 1,
                                    '&:hover': {
                                        bgcolor: 'rgba(0,0,0,0.9)',
                                        transform: 'translateY(-50%) scale(1.1)'
                                    }
                                }}
                                onClick={handlePrevImage}
                                disabled={selectedImage === 0}
                            >
                                <ChevronLeft sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                            </IconButton>

                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    right: { xs: 16, sm: 24 },
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(0,0,0,0.8)',
                                    color: 'white',
                                    zIndex: 2,
                                    width: { xs: 44, sm: 48 },
                                    height: { xs: 44, sm: 48 },
                                    opacity: selectedImage === images.length - 1 ? 0.3 : 1,
                                    '&:hover': {
                                        bgcolor: 'rgba(0,0,0,0.9)',
                                        transform: 'translateY(-50%) scale(1.1)'
                                    }
                                }}
                                onClick={handleNextImage}
                                disabled={selectedImage === images.length - 1}
                            >
                                <ChevronRight sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                            </IconButton>
                        </>
                    )}

                    {/* Zoomed Image */}
                    <img
                        src={images[selectedImage]?.url}
                        alt={productName}
                        style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            userSelect: 'none'
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    />

                    {/* Image Counter in Zoom */}
                    {images.length > 1 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: { xs: 24, sm: 32 },
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bgcolor: 'rgba(0,0,0,0.8)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: 2,
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1, sm: 1.5 },
                                zIndex: 2
                            }}
                        >
                            <Typography variant="body2" sx={{
                                color: 'white',
                                fontWeight: 500,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}>
                                {selectedImage + 1} / {images.length}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Thumbnail Grid */}
            {images.length > 1 && (
                <>
                    {/* Desktop Thumbnails */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Grid container spacing={1}>
                            {images.map((image, index) => (
                                <Grid item xs={3} key={index}>
                                    <Card
                                        sx={{
                                            cursor: 'pointer',
                                            border: selectedImage === index ? '2px solid' : '1px solid',
                                            borderColor: selectedImage === index ? 'primary.main' : 'rgba(255,255,255,0.1)',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                transform: 'scale(1.02)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => {
                                            setSelectedImage(index);
                                            setImageLoading(true);
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            height="80"
                                            image={image.url}
                                            alt={image.alt || `${productName} ${index + 1}`}
                                            sx={{ objectFit: 'cover' }}
                                        />
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* Mobile Thumbnails - Horizontal Scroll */}
                    <Box sx={{
                        display: { xs: 'block', md: 'none' },
                        overflowX: 'auto',
                        pb: 1,
                        '&::-webkit-scrollbar': {
                            height: 4,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderRadius: 2,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'primary.main',
                            borderRadius: 2,
                        }
                    }}>
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            minWidth: 'fit-content',
                            px: 0.5
                        }}>
                            {images.map((image, index) => (
                                <Card
                                    key={index}
                                    sx={{
                                        cursor: 'pointer',
                                        border: selectedImage === index ? '2px solid' : '1px solid',
                                        borderColor: selectedImage === index ? 'primary.main' : 'rgba(255,255,255,0.1)',
                                        overflow: 'hidden',
                                        minWidth: { xs: 60, sm: 80 },
                                        width: { xs: 60, sm: 80 },
                                        height: { xs: 60, sm: 80 },
                                        flexShrink: 0,
                                        '&:active': {
                                            transform: 'scale(0.95)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                        setSelectedImage(index);
                                        setImageLoading(true);
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        height="100%"
                                        image={image.url}
                                        alt={image.alt || `${productName} ${index + 1}`}
                                        sx={{
                                            objectFit: 'cover',
                                            width: '100%',
                                            height: '100%'
                                        }}
                                    />
                                </Card>
                            ))}
                        </Box>
                    </Box>
                </>
            )}

            {/* Swipe Instruction for Mobile */}
            {images.length > 1 && (
                <Box sx={{
                    display: { xs: 'block', md: 'none' },
                    textAlign: 'center',
                    mt: 1,
                    opacity: 0.7
                }}>
                    <Typography variant="caption" sx={{
                        fontSize: '0.75rem',
                        color: 'text.secondary'
                    }}>
                        Swipe or tap arrows to navigate
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

// Main Product Detail Component
const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
    const [selectedTab, setSelectedTab] = useState(0);
    const [pendingCartProduct, setPendingCartProduct] = useState(null);
    const [addingToCart, setAddingToCart] = useState(false);
    const [removingFromCart, setRemovingFromCart] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [isWishlisted, setIsWishlisted] = useState(false);

    const t = translations[language];
    const theme = createAppTheme(true, language === 'ar');
    const [availableCategories, setAvailableCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    // Add this useEffect in the Product component
    useEffect(() => {
        fetchCategories();
    }, []);

    // Add this function in the Product component
    const fetchCategories = async () => {
        setCategoriesLoading(true);
        try {
            const response = await fetch(`${BASE_URL}/api/categories`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setAvailableCategories(data.data || []);
            } else {
                console.error('Failed to fetch categories');
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setCategoriesLoading(false);
        }
    };

    // Check if user is logged in
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (token && userData) {
            setUser(JSON.parse(userData));
            fetchCartItems();
        }
    }, []);

    // Fetch product data
    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`https://dhahabjw.com/api/products/${id}?lang=${language}`);

                if (response.data.success) {
                    setProduct(response.data.data);
                } else {
                    setError('Product not found');
                }
            } catch (err) {
                console.error('Error fetching product:', err);
                setError(err.response?.data?.error || 'Failed to load product');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchProduct();
        }
    }, [id, language]);

    // Fetch cart items to check if product is in cart
    const fetchCartItems = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.get('https://dhahabjw.com/api/cart', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                setCartItems(response.data.data.items || []);
            }
        } catch (error) {
            console.error('Error fetching cart items:', error);
        }
    };

    // Check if product is in cart
    const isProductInCart = () => {
        return cartItems.some(item => item.product.id === product?.id);
    };

    // Get cart item for product
    const getCartItemForProduct = () => {
        return cartItems.find(item => item.product.id === product?.id);
    };

    // Add to Cart function
    const handleAddToCart = async () => {
        const token = localStorage.getItem('token');
        if (!token || !user) {
            setAuthModalOpen(true);
            return;
        }

        try {
            setAddingToCart(true);
            const response = await axios.post(
                'https://dhahabjw.com/api/cart/add',
                {
                    productId: product.id,
                    quantity: quantity,
                    ...((product.sizes || []).filter(s => s.isActive !== false).length > 0
                        ? { sizeIndex: selectedSizeIndex }
                        : {})
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Product added to cart successfully!',
                    severity: 'success'
                });

                // Update cart count in header
                window.dispatchEvent(new CustomEvent('cartUpdated', {
                    detail: { itemCount: response.data.data?.cartItemCount }
                }));

                await fetchCartItems();
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to add product to cart',
                severity: 'error'
            });
        } finally {
            setAddingToCart(false);
        }
    };

    // Remove from Cart function
    const handleRemoveFromCart = async () => {
        const token = localStorage.getItem('token');
        if (!token || !user) {
            setAuthModalOpen(true);
            return;
        }

        const cartItem = getCartItemForProduct();
        if (!cartItem) return;

        try {
            setRemovingFromCart(true);
            const response = await axios.delete(
                `https://dhahabjw.com/api/cart/remove/${cartItem.id}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Product removed from cart successfully!',
                    severity: 'success'
                });

                window.dispatchEvent(new CustomEvent('cartUpdated', {
                    detail: { itemCount: cartItems.length - 1 }
                }));

                await fetchCartItems();
            }
        } catch (error) {
            console.error('Error removing from cart:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to remove product from cart',
                severity: 'error'
            });
        } finally {
            setRemovingFromCart(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleAuthModalClose = () => {
        setAuthModalOpen(false);
        setPendingCartProduct(null);
    };

    // Enhanced login success handler
    const handleLoginSuccess = async () => {
        // Update user state
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }

        // Fetch cart items after login
        await fetchCartItems();

        // If there was a pending cart addition, execute it now
        if (pendingCartProduct) {
            // Wait a bit for the auth state to settle
            setTimeout(async () => {
                try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                        console.error('No token found after login');
                        return;
                    }

                    setAddingToCart(prev => ({ ...prev, [pendingCartProduct]: true }));

                    const response = await axios.post(
                        'https://dhahabjw.com/api/cart/add',
                        {
                            productId: pendingCartProduct,
                            quantity: 1,
                            ...(product && product.id === pendingCartProduct &&
                                (product.sizes || []).filter(s => s.isActive !== false).length > 0
                                ? { sizeIndex: selectedSizeIndex }
                                : {})
                        },
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            timeout: 10000
                        }
                    );

                    if (response.data.success) {
                        setSnackbar({
                            open: true,
                            message: response.data.message || t.addedToCart || 'Product added to cart successfully!',
                            severity: 'success'
                        });
                        window.dispatchEvent(new CustomEvent('cartUpdated', {
                            detail: { itemCount: response.data.data?.cartItemCount }
                        }));
                        await fetchCartItems(); // Refresh cart items
                    }
                } catch (error) {
                    console.error('Error adding to cart after login:', error);
                    setSnackbar({
                        open: true,
                        message: error.response?.data?.error || t.addToCartError || 'Failed to add product to cart',
                        severity: 'error'
                    });
                } finally {
                    setAddingToCart(prev => ({ ...prev, [pendingCartProduct]: false }));
                    setPendingCartProduct(null);
                }
            }, 1000);
        }
    };

    if (loading) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                    <AppHeader
                        language={language}
                        setLanguage={setLanguage}
                        drawerOpen={drawerOpen}
                        setDrawerOpen={setDrawerOpen}
                        t={t}
                    />
                    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, md: 4 } }}>
                        <Grid container spacing={{ xs: 2, md: 6 }}>
                            <Grid item xs={12} md={6}>
                                <Skeleton variant="rectangular" height={{ xs: 300, sm: 400, md: 500 }} sx={{ mb: 2 }} />
                                <Grid container spacing={1}>
                                    {[...Array(4)].map((_, i) => (
                                        <Grid item xs={3} key={i}>
                                            <Skeleton variant="rectangular" height={{ xs: 60, sm: 80 }} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Skeleton variant="text" height={{ xs: 40, md: 60 }} />
                                <Skeleton variant="text" height={{ xs: 30, md: 40 }} />
                                <Skeleton variant="text" height={{ xs: 60, md: 80 }} />
                                <Skeleton variant="rectangular" height={{ xs: 150, md: 200 }} />
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            </ThemeProvider>
        );
    }

    if (error || !product) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                    <AppHeader
                        language={language}
                        setLanguage={setLanguage}
                        drawerOpen={drawerOpen}
                        setDrawerOpen={setDrawerOpen}
                        t={t}
                    />
                    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, md: 4 } }}>
                        <Box textAlign="center" py={{ xs: 4, md: 8 }}>
                            <Typography variant="h4" color="error" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                {error || t.productNotFound}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBack />}
                                onClick={() => navigate('/store')}
                                sx={{ mt: 2, fontSize: { xs: '0.875rem', md: '1rem' } }}
                                fullWidth={{ xs: true, sm: false }}
                            >
                                {t.backToStore}
                            </Button>
                        </Box>
                    </Container>
                </Box>
            </ThemeProvider>
        );
    }

    const isInCart = isProductInCart();
    const isLowStock = product.inventory?.quantity <= (product.inventory?.lowStockThreshold || 5);
    const category = availableCategories.filter((item) => { return item._id == product.category; });

    // ----- Size variants: derive the selected size, weight and live price -----
    const activeSizes = (product.sizes || []).filter(s => s.isActive !== false);
    const hasSizes = activeSizes.length > 0;
    const metalRate = product.pricing?.currentMetalRate || 0;
    const sizeDiscount = product.pricing?.discount || 0;
    const selectedSize = hasSizes ? (activeSizes[selectedSizeIndex] || activeSizes[0]) : null;
    const computeSizePrice = (s) => {
        const base = (metalRate + (s.makingCharges || 0)) * (s.weight || 0);
        return base - (base * sizeDiscount / 100);
    };
    const displayWeight = selectedSize ? selectedSize.weight : product.weight;
    const displayPrice = selectedSize ? computeSizePrice(selectedSize) : (product.pricing?.finalPrice || 0);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', color: 'text.primary' }}>
                <AppHeader
                    language={language}
                    setLanguage={setLanguage}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    t={t}
                />

                <MobileDrawer
                    language={language}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    t={t}
                />

                {/* Floating Action Button for Cart */}
                <Fab
                    color="primary"
                    sx={{
                        position: 'fixed',
                        bottom: 80,
                        right: 20,
                        zIndex: 1000,
                        display: { xs: 'flex', md: 'none' }
                    }}
                    onClick={() => navigate('/cart')}
                >
                    <ShoppingBag />
                </Fab>

                <Container maxWidth="xl" sx={{ py: 4 }}>

                    {/* Main Product Section */}
                    <Grid container spacing={6}>
                        {/* Product Images */}
                        <Grid item xs={12} md={6}>
                            <ImageGallery images={product.images} productName={product.name} />
                        </Grid>

                        {/* Product Info */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ position: 'sticky', top: 20 }}>
                                {/* Product Title */}
                                <Typography variant="h4" sx={{
                                    fontWeight: 700,
                                    mb: 2,
                                    color: 'text.primary',
                                    lineHeight: 1.2
                                }}>
                                    {product.name}
                                </Typography>

                                {/* Product Category & Status */}
                                <Box display="flex" gap={1} mb={3}>
                                    <Chip
                                        label={category.length > 0 ? category[0].name.en : ''}
                                        color="secondary" />
                                    <Chip label={product.karat} variant="outlined" />
                                    {product.pricing?.isOnSale && (
                                        <Chip
                                            label={`${product.pricing.discount}% ${t.off}`}
                                            color="primary"
                                            sx={{ bgcolor: '#F8D25C', color: '#000' }}
                                        />
                                    )}
                                    {isInCart && (
                                        <Chip
                                            label={t.inCart || 'In Cart'}
                                            color="success"
                                            icon={<CheckCircle />}
                                        />
                                    )}
                                </Box>

                                {/* Price Section */}
                                <Box mb={4}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t.price}</Typography>
                                    <Typography variant="h3" color="primary.main" sx={{
                                        fontWeight: 700,
                                        fontSize: '1.5rem'
                                    }}>
                                        {displayPrice?.toLocaleString('en-US', {
                                            minimumFractionDigits: 3,
                                            maximumFractionDigits: 3
                                        }) + ' ' + t.kwd}
                                    </Typography>
                                    {product.pricing?.isOnSale && product.pricing?.originalPrice && (
                                        <Typography variant="h6" color="text.secondary" sx={{
                                            textDecoration: 'line-through',
                                            mt: 1
                                        }}>
                                            {product.pricing.originalPrice.toLocaleString('en-US', {
                                                minimumFractionDigits: 3,
                                                maximumFractionDigits: 3
                                            }) + ' ' + t.kwd}
                                        </Typography>
                                    )}
                                    {/* EMI Price */}
                                    <Box sx={{
                                        mt: 2,
                                        bgcolor: 'rgba(0, 0, 0, 0.1)',
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: '1px solid white'
                                    }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {t.emiOption || 'Installment Option'}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {t.emiDescription?.replace('{amount}', (displayPrice / 4).toLocaleString('en-US', {
                                                minimumFractionDigits: 3,
                                                maximumFractionDigits: 3
                                            }))?.replace('{currency}', t.kwd) ||
                                                `As low as ${(displayPrice / 4).toLocaleString('en-US', {
                                                    minimumFractionDigits: 3,
                                                    maximumFractionDigits: 3
                                                })} ${t.kwd}/month or 4 interest-free payments.`}

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {t.learnMore || 'Learn more'}
                                                <img
                                                    src={require("../images/deema.png")}
                                                    alt="Deema"
                                                    style={{
                                                        height: '24px',
                                                        width: 'auto',
                                                        verticalAlign: 'middle'
                                                    }}
                                                />
                                            </Box>
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Size / Weight Options */}
                                {hasSizes && (
                                    <Box mb={2}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {t.selectSize || 'Select Size'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {activeSizes.map((s, i) => {
                                                const label = s.label || `${s.weight}${t.grams ? '' : 'g'}`;
                                                const selected = i === selectedSizeIndex;
                                                return (
                                                    <Chip
                                                        key={i}
                                                        label={`${label}${!s.label ? ` ${t.grams || 'g'}` : ''}`}
                                                        onClick={() => setSelectedSizeIndex(i)}
                                                        color={selected ? 'primary' : 'default'}
                                                        variant={selected ? 'filled' : 'outlined'}
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: selected ? 'black' : 'text.primary',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}

                                {/* Product Weight */}
                                {displayWeight && (
                                    <Box mb={2}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {t.weight || 'Weight'}
                                        </Typography>
                                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                            {displayWeight} {t.grams || 'grams'}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Description */}
                                {product.description && (
                                    <Box mb={2}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {t.description || 'Description'}
                                        </Typography>
                                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                            {product.description}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Action Buttons */}
                                <Stack spacing={3} mb={4}>
                                    {isInCart ? (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="large"
                                            startIcon={removingFromCart ? <CircularProgress size={20} color="inherit" /> : <RemoveShoppingCart />}
                                            onClick={handleRemoveFromCart}
                                            disabled={removingFromCart}
                                            sx={{
                                                py: 2,
                                                fontSize: '1.1rem',
                                                fontWeight: 600,
                                                borderColor: 'error.main',
                                                '&:hover': {
                                                    bgcolor: 'error.main',
                                                    color: 'white'
                                                }
                                            }}
                                        >
                                            {removingFromCart ? (t.removing || 'Removing...') : (t.removeFromCart || 'Remove from Cart')}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={addingToCart ? <CircularProgress size={20} color="inherit" /> : <AddShoppingCart />}
                                            onClick={handleAddToCart}
                                            disabled={product.inventory?.quantity < 1 || addingToCart}
                                            sx={{
                                                py: 2,
                                                fontSize: '1.1rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            {addingToCart ? (t.adding || 'Adding...') : (t.addToCart || 'Add to Cart')}
                                        </Button>
                                    )}

                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => navigate('/store')}
                                        sx={{
                                            py: 2,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            borderColor: 'primary.main',
                                            color: 'primary.main',
                                            '&:hover': {
                                                bgcolor: 'rgba(212, 175, 55, 0.1)',
                                                borderColor: 'primary.main'
                                            }
                                        }}
                                    >
                                        {t.continueShopping}
                                    </Button>
                                </Stack>
                                {/* Stock Status */}
                                {isLowStock && (
                                    <Alert
                                        severity="warning"
                                        sx={{
                                            mb: 3,
                                            bgcolor: 'rgba(255, 193, 7, 0.1)',
                                            border: '1px solid rgba(255, 193, 7, 0.3)'
                                        }}
                                    >{t.onlyXLeft.replace('{count}', product.inventory?.quantity)}</Alert>
                                )}

                                {/* Product Features */}
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Box textAlign="center" p={2} sx={{
                                            bgcolor: 'background.paper',
                                            borderRadius: 2,
                                            border: '1px solid rgba(255,255,255,0.1)'
                                        }}>
                                            <LocalShipping sx={{ color: 'primary.main', mb: 1, fontSize: 28 }} />
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 500 }}>{t.freeShipping}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box textAlign="center" p={2} sx={{
                                            bgcolor: 'background.paper',
                                            borderRadius: 2,
                                            border: '1px solid rgba(255,255,255,0.1)'
                                        }}>
                                            <Security sx={{ color: 'primary.main', mb: 1, fontSize: 28 }} />
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 500 }}>{t.securePayment}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box textAlign="center" p={2} sx={{
                                            bgcolor: 'background.paper',
                                            borderRadius: 2,
                                            border: '1px solid rgba(255,255,255,0.1)'
                                        }}>
                                            <VerifiedUser sx={{ color: 'primary.main', mb: 1, fontSize: 28 }} />
                                            <Typography variant="caption" display="block" sx={{ fontWeight: 500 }}>{t.authenticity}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
                <AuthModal
                    open={authModalOpen}
                    onClose={handleAuthModalClose}
                    language={language}
                    t={t}
                    onLoginSuccess={handleLoginSuccess}
                />

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
                <AppFooter t={t} />
            </Box>
        </ThemeProvider>
    );
};

export default ProductDetail;